import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';
import {
  MARKET_CITIES,
  PROGRAMMATIC_VERTICALS,
  getProgrammaticCityRouteConfigs,
  getProgrammaticVerticalBySlug,
} from '@/lib/programmaticData';

const FinancingSolutionsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#f8f5ef] pb-24 text-slate-900">
      <SEO
        title="Financing Solutions by Industry"
        description="Explore financing-enabled checkout playbooks for vintage furniture sellers, tattoo studios, contractors, med spas, and more."
        canonical="/financing"
      />

      <section className="px-6 pb-16 pt-28 md:pt-32">
        <div className="container mx-auto max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Industry Solutions</p>
          <h1 className="mt-3 font-sans text-4xl text-slate-900 md:text-6xl">Financing playbooks built for high-ticket sellers</h1>
          <p className="mt-4 max-w-3xl text-lg text-slate-600">
            Choose your business type to see how LendLifted can move buyers from quote hesitation to confident checkout with monthly payment options.
          </p>
        </div>
      </section>

      <section className="px-6">
        <div className="container mx-auto grid max-w-6xl gap-4 md:grid-cols-2">
          {PROGRAMMATIC_VERTICALS.map((vertical, idx) => (
            <motion.div
              key={vertical.slug}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ delay: idx * 0.04, duration: 0.35 }}
            >
              <Card className="h-full rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md">
                <CardContent className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{vertical.industry}</p>
                  <h2 className="mt-2 font-sans text-2xl text-slate-900">{vertical.title}</h2>
                  <p className="mt-3 text-sm text-slate-600">{vertical.summary}</p>
                  <div className="mt-5">
                    <Button asChild variant="outline" className="rounded-full border-slate-300 bg-transparent">
                      <Link to={`/financing/${vertical.slug}`}>
                        View playbook
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-6 py-12">
        <div className="container mx-auto max-w-6xl rounded-3xl border border-slate-200 bg-white p-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-sans text-2xl text-slate-900">City-specific pages</h2>
            <Button asChild variant="outline" className="rounded-full border-slate-300 bg-transparent">
              <Link to="/calculators">Open calculator hub</Link>
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {getProgrammaticCityRouteConfigs()
              .slice(0, 6)
              .map((config) => {
                const city = MARKET_CITIES.find((item) => item.slug === config.citySlug);
                const vertical = getProgrammaticVerticalBySlug(config.verticalSlug);
                if (!city || !vertical) return null;
                return (
              <Link
                key={`${config.verticalSlug}-${city.slug}`}
                to={`/financing/${config.verticalSlug}/${city.slug}`}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
              >
                {vertical.industry} in {city.city}
              </Link>
                );
              })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancingSolutionsPage;
